import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import ReactDOMServer from "react-dom/server";
import {
  AppDatePicker,
  AppFileUploader,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from "@/layout/AppComponents";
import Translator from "../../../../../components/helpers/Translator";
import { axiosPath } from "../../../../../../routing/helpers/router";
import minions from "../../../../../../minions/minions";
import errorHandler from "../../../../../../layout/components/error/ErrorHandler";
import AppFileUploaderList from "../../../../../components/form/File/AppFileUploaderList";
import useIsMounted from "../../../../../components/hooks/useIsMounted";
import { handleMinDate } from '../../tools/comsaReportsTools';

export default function ModuleComsaWasteRecoveryUpdate(props) {
  const { report, onRechargeReports } = props;
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [showModalReport, setShowModalReport] = useState(false);
  const [months] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: <Translator file="user" name={`calendar.month${i + 1}`} />,
      value: i + 1,
    }))
  );
  const validationSchema = Yup.object().shape({
    month: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    totalWasteGenerated: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    wasteSentRecycling: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    wasteResidualFraction: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
  });

  const handleUpdateReport = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaReports_editReport"),
          minions.forms.toFormData({
            ...payload,
            id: report.id,
            type: "wasteRecovery",
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(() => {
          if (isMounted.current) onRechargeReports();
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setShowModalReport(false)),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  return (
    <>
      <span
        className="btn btn-info btn-xs mr-1"
        onClick={() => setShowModalReport(true)}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="edit-waste-recovery" />,
        )}
      >
        <AppRemixIcon icon="edit" />
      </span>

      <AppModal
        show={showModalReport}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="edit-waste-recovery" />,
        )}
        size="lg"
        onClose={() => setShowModalReport(false)}
      >
        <Formik
          initialValues={{
            year: report.year,
            month: parseInt(report.month),
            totalWasteGenerated: report.totalWasteGenerated,
            wasteSentRecycling: report.wasteSentRecycling,
            wasteResidualFraction: report.wasteResidualFraction,
            files: [],
            deletedFiles: [],
            comment: report.comment,
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleUpdateReport}
        >
          {({ setFieldValue, handleSubmit, values, errors }) => (
            <>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <AppDatePicker
                      title={<Translator file="module-comsaReports" name="year" />}
                      value={values.year}
                      onChange={(opt) => setFieldValue('year', opt)}
                      minDate={handleMinDate(report)}
                      dateFormat="yyyy"
                      displayDateFormat="YYYY"
                      placeholder={
                        <Translator file="module-comsaReports" name="select.a.year" />
                      }
                      isClearable
                      showYearPicker
                      popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      error={errors.year}
                    />

                    <AppInputNumber
                      icon={<AppRemixIcon icon="asterisk" noVariants />}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaReports"
                          name="total-amount-waste-generated"
                        />,
                      )}
                      value={values.totalWasteGenerated}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="value-kilos" />,
                      )}
                      onChange={(v) => setFieldValue('totalWasteGenerated', v)}
                      isClearable
                      error={errors.totalWasteGenerated}
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <AppSingleSelect
                      options={months}
                      onChange={(opt) => setFieldValue('month', opt)}
                      title={<Translator file="module-saicaReports" name="month" />}
                      icon={<AppRemixIcon icon="calendar" />}
                      placeholder={
                        <Translator
                          file="module-comsaReports"
                          name="select.a.month"
                        />
                      }
                      value={values.month}
                      error={errors.month}
                    />

                    <AppInputNumber
                      icon={<AppRemixIcon icon="truck" />}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaReports"
                          name="quantity-waste-sent-recycling"
                        />,
                      )}
                      value={values.wasteSentRecycling}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="value-kilos" />,
                      )}
                      onChange={(v) => setFieldValue('wasteSentRecycling', v)}
                      isClearable
                      error={errors.wasteSentRecycling}
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <AppInputNumber
                      icon={<AppRemixIcon icon="delete-bin-3" />}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaReports"
                          name="quantity-residual-waste-fraction"
                        />,
                      )}
                      value={values.wasteResidualFraction}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="value-kilos" />,
                      )}
                      onChange={(v) => setFieldValue('wasteResidualFraction', v)}
                      isClearable
                      error={errors.wasteResidualFraction}
                    />
                  </div>
                </div>
                <div className="col-12">
                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="m-cr-comment" />,
                    )}
                    value={values?.comment}
                    onChange={(v) => setFieldValue(`comment`, v)}
                    height="89px"
                  />
                </div>
              </div>

              <div>
                <div className="text-primary mt-3">
                  <Translator file="module-comsaReports" name="attach.evidence" />
                </div>

                <div className="row mb-3">
                  <div className="col-sm-12 col-lg-6">
                    <AppFileUploader
                      isMulti
                      placeholder={
                        !values.files || Object.keys(values?.files).length === 0
                          ? ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="select.one.multiple.files"
                              />,
                            )
                          : ReactDOMServer.renderToString(
                              <Translator
                                file="general"
                                name="select.number.of.elements"
                                variables={{
                                  elements: Object.keys(values?.files).length,
                                }}
                              />,
                            )
                      }
                      onChange={(opt) => setFieldValue('files', opt)}
                      btnText={<Translator file="general" name="search..." />}
                    />
                  </div>

                  <div className="col-sm-12 col-lg-6">
                    <AppFileUploaderList
                      values={values}
                      setFieldValue={setFieldValue}
                      urlsAvaliable={report.files}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <button
                    className="btn btn-success btn-block"
                    type="submit"
                    onClick={handleSubmit}
                  >
                    <Translator file="module-comsaReports" name="edit" />
                  </button>
                </div>
              </div>
            </>
          )}
        </Formik>
      </AppModal>
    </>
  );
}
